<template>
  <div class="live-event-page">
    <div class="filter-tools">
      <el-input v-model="keyword" placeholder="搜索赛事名称、地点、品牌" clearable class="query-input" @clear="resetSearch">
        <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
      </el-input>
      <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新增直播</el-button>
    </div>

    <el-table ref="liveEventTable" v-loading="loading" :data="tableData" style="width: 100%">
      <el-table-column type="index" width="50"></el-table-column>
      <el-table-column prop="_id" label="直播ID" width="170">
        <template slot-scope="scope">
          <copy-text :value="scope.row._id"></copy-text>
        </template>
      </el-table-column>
      <el-table-column prop="eventName" label="关联赛事" min-width="260">
        <template slot-scope="scope">
          <copy-text :value="scope.row.eventName"></copy-text>
        </template>
      </el-table-column>
      <el-table-column label="页面类型" width="110">
        <template slot-scope="scope">
          <el-tag :type="scope.row.pageType === 'featured' ? 'warning' : 'info'">
            {{ scope.row.pageType === 'featured' ? '重点专题' : '普通直播' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="address" label="比赛地点" min-width="160"></el-table-column>
      <el-table-column label="直播时间" width="280">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.startTime) }} - {{ formatDateTime(scope.row.endTime) }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="statusTagType(scope.row)">{{ liveStatusText(scope.row) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="渠道" min-width="240">
        <template slot-scope="scope">
          <el-tag
            v-for="item in enabledChannelLabels(scope.row.channels)"
            :key="item"
            size="mini"
            class="channel-tag"
          >{{ item }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="品牌关键词" prop="brandKeyword" width="140"></el-table-column>
      <el-table-column label="操作" fixed="right" :width="operationColumnWidth">
        <template slot-scope="scope">
          <div class="live-actions">
            <el-button size="mini" icon="el-icon-edit" title="编辑" @click="openEditDialog(scope.row)">
              <span class="live-action-text">编辑</span>
            </el-button>
            <el-button size="mini" type="success" icon="el-icon-document-copy" title="复制" @click="openCopyDialog(scope.row)">
              <span class="live-action-text">复制</span>
            </el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" title="删除" @click="handleDelete(scope.row)">
              <span class="live-action-text">删除</span>
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > 0"
      class="pager"
      background
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page.sync="pageNum"
      @current-change="onCurrentPageChange"
    ></el-pagination>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="820px"
      custom-class="mobile-form-dialog"
      :close-on-click-modal="false"
      @close="resetForm"
    >
      <el-form ref="form" v-loading="saving || uploading" :model="form" label-width="130px">
        <el-form-item label="页面类型" required>
          <el-radio-group v-model="form.pageType">
            <el-radio-button label="standard">普通直播</el-radio-button>
            <el-radio-button label="featured">重点专题</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="关联赛事" :required="form.pageType === 'standard'">
          <el-select
            v-model="form.eventId"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="搜索并选择赛事"
            :remote-method="searchEvents"
            :loading="eventLoading"
            :popper-append-to-body="true"
            popper-class="live-event-select-dropdown"
            class="event-select"
            @change="onEventChange"
            @visible-change="onEventSelectVisibleChange"
          >
            <el-option
              v-for="event in eventOptions"
              :key="event._id"
              :label="event.name"
              :value="event._id"
            >
              <div class="event-option">
                <div class="event-option-name">{{ event.name }}</div>
                <div class="event-option-meta">
                  <span>{{ formatEventOptionTime(event) }}</span>
                  <span>{{ event.address || '暂无地点' }}</span>
                </div>
              </div>
            </el-option>
            <el-option
              v-if="eventOptions.length && (eventLoadingMore || !eventHasMore)"
              :key="eventLoadingMore ? 'loading-more' : 'no-more'"
              disabled
              :label="eventLoadingMore ? '加载更多赛事...' : '没有更多赛事了'"
              :value="eventLoadingMore ? 'loading-more' : 'no-more'"
            >
              <div class="event-option-status">
                {{ eventLoadingMore ? '加载更多赛事...' : '没有更多赛事了' }}
              </div>
            </el-option>
          </el-select>
          <div v-if="form.pageType === 'featured'" class="field-tip">重点专题可不关联现有赛事，标题、地点和时间需手动填写。</div>
        </el-form-item>

        <el-form-item label="直播展示名称" required>
          <el-input v-model="form.eventName" placeholder="选择赛事后自动带出，可修改">
            <copy-text slot="append" :value="form.eventName" icon-only></copy-text>
          </el-input>
        </el-form-item>

        <el-form-item label="比赛地点" required>
          <el-input v-model="form.address" placeholder="选择赛事后自动带出，可修改"></el-input>
        </el-form-item>

        <el-form-item label="比赛时间" required>
          <el-date-picker
            v-model="form.timeRange"
            type="datetimerange"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            class="time-range"
          ></el-date-picker>
          <div class="field-tip">
            若时间为 00:00:00，前端仅展示日期；若开始时间和结束时间完全一致，前端展示为“起直播”，表示结束时间不明确。
          </div>
        </el-form-item>

        <el-form-item label="所属赛事品牌">
          <el-input v-model="form.brandKeyword" placeholder="输入关键词，如 IFBB、寰际、WNBF"></el-input>
          <div class="field-tip">
            C端优先以 brandConfig 的品牌列表为准，moduleConfig 用于补充品牌全称、logo 等详情；会用该关键词匹配 query、简称、全称、id、value，支持互相包含匹配。若未匹配到品牌页，点击“该品牌全部赛事”会跳转赛事搜索页并自动搜索该关键词。
          </div>
        </el-form-item>

        <el-form-item v-if="form.pageType === 'featured'" label="赛程模式" required>
          <el-radio-group v-model="form.topicConfig.schedule.mode" @change="handleScheduleModeChange">
            <el-radio-button label="image">长图模式</el-radio-button>
            <el-radio-button label="timeline">时间节点模式</el-radio-button>
          </el-radio-group>
          <div class="field-tip">长图模式点击后展开图片；时间节点模式展示可添加到手机日历的赛程时间轴。</div>
        </el-form-item>

        <el-form-item
          v-if="form.pageType === 'standard' || form.topicConfig.schedule.mode === 'image'"
          label="赛程图片"
        >
          <div class="upload-row">
            <el-upload
              class="live-image-upload"
              action=""
              :show-file-list="false"
              :http-request="(param) => uploadImage(param, 'scheduleImage')"
            >
              <img v-if="form.scheduleImage" :src="form.scheduleImage" class="preview-img" />
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus"></i>
                <span>上传图片</span>
              </div>
            </el-upload>
            <el-button v-if="form.scheduleImage" size="mini" type="text" @click="form.scheduleImage = ''">清除</el-button>
          </div>
        </el-form-item>

        <el-form-item
          v-if="form.pageType === 'featured' && form.topicConfig.schedule.mode === 'timeline'"
          label="赛程节点"
          required
        >
          <div
            v-for="(node, index) in form.topicConfig.schedule.nodes"
            :key="index"
            class="schedule-node-editor"
          >
            <div class="schedule-node-head">
              <strong>时间节点 {{ index + 1 }}</strong>
              <el-button type="text" class="danger-text" @click="removeScheduleNode(index)">删除</el-button>
            </div>
            <div class="schedule-node-field-label">开始时间</div>
            <el-date-picker
              v-model="node.startTime"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="选择节点开始时间"
              class="schedule-node-field"
            ></el-date-picker>
            <div class="schedule-node-field-label">标题</div>
            <el-input
              v-model.trim="node.title"
              maxlength="100"
              placeholder="节点标题，如：男子古典健美预赛"
              class="schedule-node-field"
            ></el-input>
            <div class="schedule-node-field-label">内容</div>
            <el-input
              v-model="node.content"
              type="textarea"
              :rows="4"
              maxlength="5000"
              show-word-limit
              placeholder="节点内容，支持换行"
              class="schedule-node-field"
            ></el-input>
            <div class="schedule-calendar-switch">
              <el-switch v-model="node.calendarEnabled"></el-switch>
              <span>允许添加到手机日历，比赛开始时提醒</span>
            </div>
          </div>
          <el-button type="primary" plain size="small" icon="el-icon-plus" @click="addScheduleNode">
            添加时间节点
          </el-button>
          <div class="field-tip">专题页按开始时间从早到晚展示；节点内容支持换行。</div>
        </el-form-item>

        <el-form-item label="直播海报">
          <div class="upload-row">
            <el-upload
              class="live-image-upload"
              action=""
              :show-file-list="false"
              :http-request="(param) => uploadImage(param, 'livePoster')"
            >
              <img v-if="form.livePoster" :src="form.livePoster" class="preview-img" />
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus"></i>
                <span>上传海报</span>
              </div>
            </el-upload>
            <div class="upload-side">
              <el-button v-if="form.livePoster" size="mini" type="text" @click="form.livePoster = ''">清除</el-button>
              <span class="upload-tip">{{ form.pageType === 'featured' ? '重点专题必填' : '可选，不上传则使用赛事头图' }}</span>
            </div>
          </div>
        </el-form-item>

        <el-divider>直播渠道</el-divider>

        <div class="channel-block">
          <el-form-item label="抖音直播">
            <el-switch v-model="form.channels.douyin.enabled"></el-switch>
          </el-form-item>
          <template v-if="form.channels.douyin.enabled">
            <el-form-item label="抖音账号">
              <el-input v-model="form.channels.douyin.account" placeholder="请输入抖音账号"></el-input>
            </el-form-item>
            <el-form-item label="抖音链接">
              <el-input v-model="form.channels.douyin.url" placeholder="可选，填写直播间或主页链接"></el-input>
            </el-form-item>
            <el-form-item label="抖音二维码">
              <div class="upload-row">
                <el-upload
                  class="qrcode-upload"
                  action=""
                  :show-file-list="false"
                  :http-request="(param) => uploadChannelQrcode('douyin', param)"
                >
                  <img v-if="form.channels.douyin.qrcode" :src="form.channels.douyin.qrcode" class="qrcode-preview" />
                  <div v-else class="qrcode-placeholder">
                    <i class="el-icon-plus"></i>
                    <span>上传二维码</span>
                  </div>
                </el-upload>
                <el-button v-if="form.channels.douyin.qrcode" size="mini" type="text" @click="form.channels.douyin.qrcode = ''">清除</el-button>
              </div>
            </el-form-item>
          </template>
        </div>

        <div class="channel-block">
          <el-form-item label="微信视频号直播">
            <el-switch v-model="form.channels.wechatChannels.enabled"></el-switch>
          </el-form-item>
          <template v-if="form.channels.wechatChannels.enabled">
            <el-form-item label="视频号账号名称">
              <el-input v-model="form.channels.wechatChannels.account" placeholder="请输入视频号账号名称"></el-input>
            </el-form-item>
            <el-form-item label="视频号链接">
              <el-input v-model="form.channels.wechatChannels.url" placeholder="可选，填写可复制的观看链接"></el-input>
            </el-form-item>
            <el-form-item label="视频号二维码">
              <div class="upload-row">
                <el-upload
                  class="qrcode-upload"
                  action=""
                  :show-file-list="false"
                  :http-request="(param) => uploadChannelQrcode('wechatChannels', param)"
                >
                  <img v-if="form.channels.wechatChannels.qrcode" :src="form.channels.wechatChannels.qrcode" class="qrcode-preview" />
                  <div v-else class="qrcode-placeholder">
                    <i class="el-icon-plus"></i>
                    <span>上传二维码</span>
                  </div>
                </el-upload>
                <el-button v-if="form.channels.wechatChannels.qrcode" size="mini" type="text" @click="form.channels.wechatChannels.qrcode = ''">清除</el-button>
              </div>
            </el-form-item>
          </template>
        </div>

        <div class="channel-block">
          <el-form-item label="Bilibili直播">
            <el-switch v-model="form.channels.bilibili.enabled"></el-switch>
          </el-form-item>
          <template v-if="form.channels.bilibili.enabled">
            <el-form-item label="Bilibili账号">
              <el-input v-model="form.channels.bilibili.account" placeholder="请输入 Bilibili 账号"></el-input>
            </el-form-item>
            <el-form-item label="Bilibili链接">
              <el-input v-model="form.channels.bilibili.url" placeholder="可选，填写直播间或主页链接"></el-input>
            </el-form-item>
            <el-form-item label="Bilibili二维码">
              <div class="upload-row">
                <el-upload
                  class="qrcode-upload"
                  action=""
                  :show-file-list="false"
                  :http-request="(param) => uploadChannelQrcode('bilibili', param)"
                >
                  <img v-if="form.channels.bilibili.qrcode" :src="form.channels.bilibili.qrcode" class="qrcode-preview" />
                  <div v-else class="qrcode-placeholder">
                    <i class="el-icon-plus"></i>
                    <span>上传二维码</span>
                  </div>
                </el-upload>
                <el-button v-if="form.channels.bilibili.qrcode" size="mini" type="text" @click="form.channels.bilibili.qrcode = ''">清除</el-button>
              </div>
            </el-form-item>
          </template>
        </div>

        <div class="channel-block">
          <el-form-item :label="form.pageType === 'featured' ? '官方直播链接' : '视频直播链接'">
            <el-switch v-model="form.channels.video.enabled"></el-switch>
          </el-form-item>
          <template v-if="form.channels.video.enabled">
            <el-form-item :label="form.pageType === 'featured' ? '官方网址' : '直播链接'" required>
              <el-input v-model="form.channels.video.url" :placeholder="form.pageType === 'featured' ? '请输入官方直播网址' : '请输入视频直播链接'"></el-input>
            </el-form-item>
            <el-form-item label="链接二维码">
              <div class="upload-row">
                <el-upload
                  class="qrcode-upload"
                  action=""
                  :show-file-list="false"
                  :http-request="(param) => uploadChannelQrcode('video', param)"
                >
                  <img v-if="form.channels.video.qrcode" :src="form.channels.video.qrcode" class="qrcode-preview" />
                  <div v-else class="qrcode-placeholder">
                    <i class="el-icon-plus"></i>
                    <span>上传二维码</span>
                  </div>
                </el-upload>
                <el-button v-if="form.channels.video.qrcode" size="mini" type="text" @click="form.channels.video.qrcode = ''">清除</el-button>
              </div>
            </el-form-item>
          </template>
        </div>

        <div class="channel-block">
          <el-form-item label="照片直播">
            <el-switch v-model="form.channels.photo.enabled" @change="handlePhotoEnabledChange"></el-switch>
          </el-form-item>
          <template v-if="form.channels.photo.enabled">
            <div
              v-for="(item, index) in form.channels.photo.items"
              :key="index"
              class="photo-stream-editor"
            >
              <div class="photo-stream-head">
                <strong>照片直播 {{ index + 1 }}</strong>
                <el-button type="text" class="danger-text" @click="removePhotoStream(index)">删除</el-button>
              </div>
              <el-form-item label="自定义名称" required>
                <el-input v-model.trim="item.name" maxlength="50" placeholder="如：官方摄影、舞台照片"></el-input>
              </el-form-item>
              <el-form-item label="直播链接" required>
                <el-input v-model.trim="item.url" placeholder="请输入照片直播链接"></el-input>
              </el-form-item>
              <el-form-item label="链接二维码">
                <div class="upload-row">
                  <el-upload
                    class="qrcode-upload"
                    action=""
                    :show-file-list="false"
                    :http-request="(param) => uploadPhotoQrcode(index, param)"
                  >
                    <img v-if="item.qrcode" :src="item.qrcode" class="qrcode-preview" />
                    <div v-else class="qrcode-placeholder">
                      <i class="el-icon-plus"></i>
                      <span>上传二维码</span>
                    </div>
                  </el-upload>
                  <el-button v-if="item.qrcode" size="mini" type="text" @click="item.qrcode = ''">清除</el-button>
                </div>
              </el-form-item>
            </div>
            <el-form-item label=" ">
              <el-button
                type="primary"
                plain
                icon="el-icon-plus"
                @click="addPhotoStream"
              >添加照片直播</el-button>
              <div class="field-tip">每组可独立设置名称、链接和二维码。</div>
            </el-form-item>
          </template>
        </div>

        <el-divider>贴图互动</el-divider>

        <el-form-item label="启用贴图组件">
          <el-switch v-model="form.stickerConfig.enabled"></el-switch>
          <div class="field-tip">微信基础库 3.9.3 及以上展示；开启后用户可直接发表和浏览赛事贴图。</div>
        </el-form-item>
        <template v-if="form.stickerConfig.enabled">
          <el-form-item label="贴图话题">
            <el-input
              v-model.trim="form.stickerConfig.topic"
              maxlength="20"
              show-word-limit
              placeholder="留空则使用小程序名称，最多 20 字"
            ></el-input>
            <div class="field-tip">修改话题后，原话题下的历史贴图不会出现在新话题中。</div>
          </el-form-item>
          <el-form-item label="推荐链接页面" required>
            <el-input
              v-model.trim="form.stickerConfig.recommendPath"
              maxlength="500"
              placeholder="例如：/pages/liveCenter/liveCenter"
            ></el-input>
            <div class="field-tip">用户发表贴图时可添加该小程序链接；须填写 pages 开头的页面路径，可携带查询参数。推荐链接能力要求微信基础库 3.16.1 及以上。</div>
          </el-form-item>
          <el-form-item label="推荐链接标题" required>
            <el-input
              v-model.trim="form.stickerConfig.recommendTitle"
              maxlength="100"
              show-word-limit
              placeholder="例如：进入赛事直播专题"
            ></el-input>
          </el-form-item>
          <el-form-item label="无内容占位文案">
            <el-input
              v-model.trim="form.stickerConfig.placeholder"
              maxlength="100"
              show-word-limit
              placeholder="晒出你的观赛瞬间，一起为选手加油"
            ></el-input>
            <div class="field-tip">仅在话题暂无内容时展示，组件内最多显示一行。</div>
          </el-form-item>
        </template>

        <template v-if="form.pageType === 'featured'">
          <el-divider>重点专题配置</el-divider>

          <el-form-item label="页面主题色" required>
            <div class="theme-color-control">
              <el-color-picker v-model="form.topicConfig.themeColor"></el-color-picker>
              <el-input
                v-model.trim="form.topicConfig.themeColor"
                maxlength="7"
                placeholder="#D7B45A"
              ></el-input>
            </div>
            <div class="field-tip">用于专题页状态、按钮、图标和边框等强调元素。</div>
          </el-form-item>

          <el-form-item label="我要上奥赛">
            <el-switch v-model="form.topicConfig.showOlympiaPath"></el-switch>
            <div class="field-tip">开启后，专题页展示自然赛道和常规赛道的奥赛职业发展路径。</div>
          </el-form-item>

          <el-form-item label="官方直播封面">
            <div class="upload-row">
              <el-upload
                class="official-cover-upload"
                action=""
                :show-file-list="false"
                :http-request="uploadOfficialCover"
              >
                <img v-if="form.topicConfig.officialCover" :src="form.topicConfig.officialCover" class="official-cover-preview" />
                <div v-else class="upload-placeholder">
                  <i class="el-icon-plus"></i>
                  <span>上传封面</span>
                </div>
              </el-upload>
              <div class="upload-side">
                <el-button v-if="form.topicConfig.officialCover" size="mini" type="text" @click="form.topicConfig.officialCover = ''">清除</el-button>
                <span class="upload-tip">建议使用 16:9 图片，未上传时使用专题主图</span>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="直播简介">
            <el-input
              v-model="form.topicConfig.description"
              type="textarea"
              :rows="5"
              maxlength="5000"
              show-word-limit
              placeholder="请输入直播简介，支持换行；专题页默认展示 3 行"
            ></el-input>
          </el-form-item>

          <el-form-item label="观看教程">
            <div v-for="(step, index) in form.topicConfig.guideSteps" :key="index" class="guide-step-editor">
              <div class="guide-step-head">
                <strong>步骤 {{ index + 1 }}</strong>
                <el-button type="text" class="danger-text" @click="removeGuideStep(index)">删除</el-button>
              </div>
              <el-input v-model="step.title" placeholder="步骤标题"></el-input>
              <el-input v-model="step.description" type="textarea" :rows="2" placeholder="步骤说明"></el-input>
              <div class="upload-row">
                <el-upload
                  class="live-image-upload"
                  action=""
                  :show-file-list="false"
                  :http-request="(param) => uploadGuideImage(index, param)"
                >
                  <img v-if="step.image" :src="step.image" class="preview-img" />
                  <div v-else class="upload-placeholder">
                    <i class="el-icon-plus"></i>
                    <span>步骤配图</span>
                  </div>
                </el-upload>
                <el-button v-if="step.image" size="mini" type="text" @click="step.image = ''">清除</el-button>
              </div>
            </div>
            <el-button type="primary" plain size="small" :disabled="form.topicConfig.guideSteps.length >= 20" @click="addGuideStep">
              添加教程步骤
            </el-button>
          </el-form-item>

          <el-form-item label="交流群标题">
            <el-input v-model="form.topicConfig.community.title" placeholder="默认：加入观赛交流群"></el-input>
          </el-form-item>
          <el-form-item label="交流群提示">
            <el-input v-model="form.topicConfig.community.tip" placeholder="例如：长按保存后使用微信扫码"></el-input>
          </el-form-item>
          <el-form-item label="交流群二维码">
            <div class="upload-row">
              <el-upload
                class="qrcode-upload"
                action=""
                :show-file-list="false"
                :http-request="uploadCommunityQrcode"
              >
                <img v-if="form.topicConfig.community.qrcode" :src="form.topicConfig.community.qrcode" class="qrcode-preview" />
                <div v-else class="qrcode-placeholder">
                  <i class="el-icon-plus"></i>
                  <span>上传二维码</span>
                </div>
              </el-upload>
              <el-button v-if="form.topicConfig.community.qrcode" size="mini" type="text" @click="form.topicConfig.community.qrcode = ''">清除</el-button>
            </div>
          </el-form-item>

          <el-form-item label="分享标题">
            <el-input v-model="form.topicConfig.share.title" placeholder="未填写时使用赛事标题"></el-input>
          </el-form-item>
          <el-form-item label="分享图片">
            <div class="upload-row">
              <el-upload
                class="live-image-upload"
                action=""
                :show-file-list="false"
                :http-request="uploadShareImage"
              >
                <img v-if="form.topicConfig.share.image" :src="form.topicConfig.share.image" class="preview-img" />
                <div v-else class="upload-placeholder">
                  <i class="el-icon-plus"></i>
                  <span>上传分享图</span>
                </div>
              </el-upload>
              <el-button v-if="form.topicConfig.share.image" size="mini" type="text" @click="form.topicConfig.share.image = ''">清除</el-button>
            </div>
          </el-form-item>

          <el-form-item label="推荐赛事">
            <el-select
              v-model="form.topicConfig.recommendedEventIds"
              multiple
              filterable
              remote
              reserve-keyword
              :multiple-limit="6"
              :remote-method="searchRecommendEvents"
              :loading="recommendLoading"
              placeholder="搜索并选择赛事，最多6场"
              class="event-select"
            >
              <el-option
                v-for="event in recommendOptions"
                :key="event._id"
                :label="event.name"
                :value="event._id"
              ></el-option>
            </el-select>
            <div class="field-tip">手选赛事优先；不足 6 场时，前端接口会自动补充当前可报名赛事。</div>
          </el-form-item>
        </template>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'loadsh';
import uploadMixin from '../../mixin/upload';

const CDN_PREFIX = 'https://cdn.jirou.fit/';
const IMAGE_SUFFIX = '~tplv-700tmnc25s-yasuo.image';

const defaultChannel = () => ({
  enabled: false,
  account: '',
  url: '',
  qrcode: ''
});

const defaultPhotoStream = () => ({
  name: '',
  url: '',
  qrcode: ''
});

const defaultPhotoChannel = () => ({
  ...defaultChannel(),
  items: []
});

const defaultScheduleNode = (startTime = '') => ({
  startTime,
  title: '',
  content: '',
  calendarEnabled: true
});

const defaultStickerConfig = () => ({
  enabled: false,
  topic: '',
  recommendPath: '/pages/liveCenter/liveCenter',
  recommendTitle: '查看更多赛事直播',
  placeholder: '晒出你的观赛瞬间，一起为选手加油'
});

export default {
  mixins: [uploadMixin],
  data() {
    return {
      loading: false,
      saving: false,
      uploading: false,
      tableData: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      keyword: '',
      dialogVisible: false,
      isEdit: false,
      isCopy: false,
      eventOptions: [],
      eventLoading: false,
      eventLoadingMore: false,
      eventSearchKeyword: '',
      eventSearchPageNum: 1,
      eventSearchPageSize: 20,
      eventSearchTotal: 0,
      eventHasMore: false,
      eventScrollWrap: null,
      searchEventSeq: 0,
      debouncedSearchEvents: null,
      recommendOptions: [],
      recommendLoading: false,
      recommendSearchSeq: 0,
      form: this.createDefaultForm(),
      isMobile: false
    };
  },
  computed: {
    operationColumnWidth() {
      return this.isMobile ? 64 : 250;
    },
    dialogTitle() {
      if (this.isEdit) return '编辑直播';
      if (this.isCopy) return '复制直播';
      return '新增直播';
    }
  },
  created() {
    this.debouncedSearchEvents = _.debounce(this.requestEvents, 350);
    this.getData();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    if (this.debouncedSearchEvents && this.debouncedSearchEvents.cancel) {
      this.debouncedSearchEvents.cancel();
    }
    this.detachEventSelectScroll();
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
      this.$nextTick(() => {
        if (this.$refs.liveEventTable && this.$refs.liveEventTable.doLayout) {
          this.$refs.liveEventTable.doLayout();
        }
      });
    },
    createDefaultForm() {
      return {
        _id: '',
        pageType: 'standard',
        eventId: '',
        eventName: '',
        address: '',
        timeRange: [],
        brandKeyword: '',
        scheduleImage: '',
        livePoster: '',
        channels: {
          douyin: defaultChannel(),
          wechatChannels: defaultChannel(),
          bilibili: defaultChannel(),
          video: defaultChannel(),
          photo: defaultPhotoChannel()
        },
        stickerConfig: defaultStickerConfig(),
        topicConfig: {
          themeColor: '#D7B45A',
          showOlympiaPath: false,
          officialCover: '',
          description: '',
          guideSteps: [],
          schedule: {
            mode: 'image',
            nodes: []
          },
          community: {
            title: '',
            tip: '',
            qrcode: ''
          },
          share: {
            title: '',
            image: ''
          },
          recommendedEventIds: []
        }
      };
    },
    getData() {
      this.loading = true;
      this.$axios
        .get('/live-event/find_all', {
          params: {
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            keyword: this.keyword
          },
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          this.tableData = _.get(res, 'data.res.list') || [];
          this.total = _.get(res, 'data.res.total') || 0;
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '直播列表获取失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleSearch() {
      this.pageNum = 1;
      this.getData();
    },
    resetSearch() {
      this.pageNum = 1;
      this.getData();
    },
    onCurrentPageChange(index) {
      this.pageNum = index;
      this.getData();
    },
    formatDateTime(date) {
      if (!date) return '-';
      return this.$dayjs(date).format('YYYY-MM-DD HH:mm');
    },
    formatEventOptionTime(event) {
      if (!event) return '暂无时间';
      if (event.fuzzyTime) return event.fuzzyTime;
      const start = event.startTime ? this.$dayjs(event.startTime).format('YYYY-MM-DD') : '';
      const end = event.endTime ? this.$dayjs(event.endTime).format('YYYY-MM-DD') : '';
      if (start && end && start !== end) return `${start} 至 ${end}`;
      return start || end || '暂无时间';
    },
    liveStatusText(row) {
      const now = Date.now();
      const start = new Date(row.startTime).getTime();
      const end = new Date(row.endTime).getTime();
      if (start === end) {
        if (now < start) return '即将直播';
        return this.isSameCalendarDay(now, start) ? '直播中' : '直播回放';
      }
      if (now >= start && now <= end) return '直播中';
      if (now < start) return '即将直播';
      return '直播回放';
    },
    isSameCalendarDay(first, second) {
      const firstDate = new Date(first);
      const secondDate = new Date(second);
      return firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getDate() === secondDate.getDate();
    },
    statusTagType(row) {
      const text = this.liveStatusText(row);
      if (text === '直播中') return 'success';
      if (text === '即将直播') return 'warning';
      return 'info';
    },
    enabledChannelLabels(channels = {}) {
      const list = [];
      if (channels.douyin && channels.douyin.enabled) list.push('抖音');
      if (channels.wechatChannels && channels.wechatChannels.enabled) list.push('视频号');
      if (channels.bilibili && channels.bilibili.enabled) list.push('Bilibili');
      if (channels.video && channels.video.enabled) list.push('视频链接');
      if (channels.photo && channels.photo.enabled) {
        const count = Array.isArray(channels.photo.items) ? channels.photo.items.length : 0;
        list.push(count > 1 ? `照片直播 × ${count}` : '照片直播');
      }
      return list;
    },
    searchEvents(keyword) {
      const text = (keyword || '').trim();
      if (!text) {
        this.resetEventSearchState();
        return;
      }
      this.eventSearchKeyword = text;
      this.eventSearchPageNum = 1;
      this.eventSearchTotal = 0;
      this.eventHasMore = false;
      this.eventLoading = true;
      this.debouncedSearchEvents(text, 1, false);
    },
    resetEventSearchState() {
      this.searchEventSeq++;
      if (this.debouncedSearchEvents && this.debouncedSearchEvents.cancel) {
        this.debouncedSearchEvents.cancel();
      }
      this.eventOptions = [];
      this.eventSearchKeyword = '';
      this.eventSearchPageNum = 1;
      this.eventSearchTotal = 0;
      this.eventHasMore = false;
      this.eventLoading = false;
      this.eventLoadingMore = false;
      this.detachEventSelectScroll();
    },
    requestEvents(keyword, pageNum = 1, append = false) {
      const seq = ++this.searchEventSeq;
      this.$axios
        .post('/admin_query_by_keyword', {
          pageNum,
          pageSize: this.eventSearchPageSize,
          name: keyword,
          includeDeleted: false
        })
        .then((res) => {
          if (seq !== this.searchEventSeq) return;
          const list = _.get(res, 'data.res.list') || [];
          const total = _.get(res, 'data.res.total') || 0;
          this.eventOptions = append ? this.mergeEventOptions(this.eventOptions, list) : list;
          this.eventSearchPageNum = pageNum;
          this.eventSearchTotal = total;
          this.eventHasMore = this.eventOptions.length < total;
        })
        .catch((err) => {
          if (seq !== this.searchEventSeq) return;
          if (!append) this.eventOptions = [];
          this.eventHasMore = false;
          this.$message.error(err.response?.data?.message || '赛事搜索失败');
        })
        .finally(() => {
          if (seq === this.searchEventSeq) {
            this.eventLoading = false;
            this.eventLoadingMore = false;
          }
        });
    },
    mergeEventOptions(current, next) {
      const exists = new Set(current.map(item => item._id));
      return current.concat(next.filter(item => !exists.has(item._id)));
    },
    loadMoreEvents() {
      if (!this.eventSearchKeyword || !this.eventHasMore || this.eventLoading || this.eventLoadingMore) return;
      this.eventLoadingMore = true;
      this.requestEvents(this.eventSearchKeyword, this.eventSearchPageNum + 1, true);
    },
    onEventSelectVisibleChange(visible) {
      if (visible) {
        this.$nextTick(this.attachEventSelectScroll);
      } else {
        this.detachEventSelectScroll();
      }
    },
    attachEventSelectScroll() {
      this.detachEventSelectScroll();
      const wrap = document.querySelector('.live-event-select-dropdown .el-select-dropdown__wrap');
      if (!wrap) return;
      this.eventScrollWrap = wrap;
      wrap.addEventListener('scroll', this.handleEventSelectScroll);
    },
    detachEventSelectScroll() {
      if (!this.eventScrollWrap) return;
      this.eventScrollWrap.removeEventListener('scroll', this.handleEventSelectScroll);
      this.eventScrollWrap = null;
    },
    handleEventSelectScroll(event) {
      const wrap = event.target;
      const distanceToBottom = wrap.scrollHeight - wrap.scrollTop - wrap.clientHeight;
      if (distanceToBottom <= 24) {
        this.loadMoreEvents();
      }
    },
    onEventChange(eventId) {
      const event = this.eventOptions.find(item => item._id === eventId);
      if (!event) return;
      this.form.eventName = event.name || '';
      this.form.address = event.address || '';
      this.form.timeRange = [
        this.formatDateTimeForPicker(event.startTime),
        this.formatDateTimeForPicker(event.endTime)
      ].filter(Boolean);
    },
    formatDateTimeForPicker(value) {
      if (!value) return '';
      return this.$dayjs(value).format('YYYY-MM-DD HH:mm:ss');
    },
    openCreateDialog() {
      this.isEdit = false;
      this.isCopy = false;
      this.form = this.createDefaultForm();
      this.recommendOptions = [];
      this.resetEventSearchState();
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.isEdit = true;
      this.isCopy = false;
      this.form = this.normalizeForm(row);
      this.setInitialEventOption(row);
      this.hydrateRecommendOptions(this.form.topicConfig.recommendedEventIds);
      this.dialogVisible = true;
    },
    openCopyDialog(row) {
      this.isEdit = false;
      this.isCopy = true;
      this.form = Object.assign(this.normalizeForm(row), {
        _id: ''
      });
      this.setInitialEventOption(row);
      this.hydrateRecommendOptions(this.form.topicConfig.recommendedEventIds);
      this.dialogVisible = true;
    },
    setInitialEventOption(row) {
      this.eventOptions = row.eventId ? [{
        _id: row.eventId,
        name: row.eventName,
        address: row.address,
        startTime: row.startTime,
        endTime: row.endTime
      }] : [];
    },
    normalizeForm(row) {
      const channels = row.channels || {};
      const stickerConfig = row.stickerConfig || {};
      const topicConfig = row.topicConfig || {};
      const schedule = topicConfig.schedule || {};
      const community = topicConfig.community || {};
      const share = topicConfig.share || {};
      return {
        _id: row._id,
        pageType: row.pageType === 'featured' ? 'featured' : 'standard',
        eventId: row.eventId || '',
        eventName: row.eventName || '',
        address: row.address || '',
        timeRange: [
          this.formatDateTimeForPicker(row.startTime),
          this.formatDateTimeForPicker(row.endTime)
        ].filter(Boolean),
        brandKeyword: row.brandKeyword || '',
        scheduleImage: row.scheduleImage || '',
        livePoster: row.livePoster || '',
        channels: {
          douyin: Object.assign(defaultChannel(), channels.douyin || {}),
          wechatChannels: Object.assign(defaultChannel(), channels.wechatChannels || {}),
          bilibili: Object.assign(defaultChannel(), channels.bilibili || {}),
          video: Object.assign(defaultChannel(), channels.video || {}),
          photo: Object.assign(defaultPhotoChannel(), channels.photo || {}, {
            items: this.normalizePhotoStreams(channels.photo)
          })
        },
        stickerConfig: Object.assign(defaultStickerConfig(), stickerConfig, {
          enabled: stickerConfig.enabled === true
        }),
        topicConfig: {
          themeColor: topicConfig.themeColor || '#D7B45A',
          showOlympiaPath: topicConfig.showOlympiaPath === true,
          officialCover: topicConfig.officialCover || '',
          description: topicConfig.description || '',
          guideSteps: (topicConfig.guideSteps || []).map(step => ({
            title: step.title || '',
            description: step.description || '',
            image: step.image || ''
          })),
          schedule: {
            mode: schedule.mode === 'timeline' ? 'timeline' : 'image',
            nodes: (schedule.nodes || []).map(node => ({
              startTime: this.formatDateTimeForPicker(node.startTime),
              title: node.title || '',
              content: node.content || '',
              calendarEnabled: node.calendarEnabled !== false
            }))
          },
          community: {
            title: community.title || '',
            tip: community.tip || '',
            qrcode: community.qrcode || ''
          },
          share: {
            title: share.title || '',
            image: share.image || ''
          },
          recommendedEventIds: (topicConfig.recommendedEventIds || []).map(String).slice(0, 6)
        }
      };
    },
    resetForm() {
      this.form = this.createDefaultForm();
      this.isCopy = false;
      this.saving = false;
      this.uploading = false;
      this.recommendOptions = [];
      this.recommendLoading = false;
      this.resetEventSearchState();
    },
    getImageUrl(data) {
      return CDN_PREFIX + data.uploadResult.ImageUri + IMAGE_SUFFIX;
    },
    uploadImage(param, field) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form[field] = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    uploadChannelQrcode(channelKey, param) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form.channels[channelKey].qrcode = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    uploadPhotoQrcode(index, param) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form.channels.photo.items[index].qrcode = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    normalizePhotoStreams(photo = {}) {
      if (Array.isArray(photo.items) && photo.items.length) {
        return photo.items.map(item => ({
          name: item.name || '',
          url: item.url || '',
          qrcode: item.qrcode || ''
        }));
      }
      if (photo.url || photo.qrcode) {
        return [{
          name: '照片直播',
          url: photo.url || '',
          qrcode: photo.qrcode || ''
        }];
      }
      return [];
    },
    handlePhotoEnabledChange(enabled) {
      if (enabled && !this.form.channels.photo.items.length) this.addPhotoStream();
    },
    addPhotoStream() {
      this.form.channels.photo.items.push(defaultPhotoStream());
    },
    removePhotoStream(index) {
      this.form.channels.photo.items.splice(index, 1);
    },
    handleScheduleModeChange(mode) {
      if (mode === 'timeline' && !this.form.topicConfig.schedule.nodes.length) {
        this.addScheduleNode();
      }
    },
    addScheduleNode() {
      const eventStartTime = this.form.timeRange && this.form.timeRange[0] ? this.form.timeRange[0] : '';
      this.form.topicConfig.schedule.nodes.push(defaultScheduleNode(eventStartTime));
    },
    removeScheduleNode(index) {
      this.form.topicConfig.schedule.nodes.splice(index, 1);
    },
    uploadGuideImage(index, param) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form.topicConfig.guideSteps[index].image = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    uploadCommunityQrcode(param) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form.topicConfig.community.qrcode = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    uploadOfficialCover(param) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form.topicConfig.officialCover = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    uploadShareImage(param) {
      this.uploading = true;
      this.uploadImg(param, {
        onSuccess: (data) => {
          this.form.topicConfig.share.image = this.getImageUrl(data);
          this.uploading = false;
        },
        onError: () => {
          this.uploading = false;
        }
      });
    },
    addGuideStep() {
      if (this.form.topicConfig.guideSteps.length >= 20) return;
      this.form.topicConfig.guideSteps.push({
        title: '',
        description: '',
        image: ''
      });
    },
    removeGuideStep(index) {
      this.form.topicConfig.guideSteps.splice(index, 1);
    },
    searchRecommendEvents(keyword) {
      const text = (keyword || '').trim();
      if (!text) return;
      const seq = ++this.recommendSearchSeq;
      this.recommendLoading = true;
      this.$axios.post('/admin_query_by_keyword', {
        pageNum: 1,
        pageSize: 20,
        name: text,
        includeDeleted: false
      }).then((res) => {
        if (seq !== this.recommendSearchSeq) return;
        const list = _.get(res, 'data.res.list') || [];
        this.recommendOptions = this.mergeEventOptions(this.recommendOptions, list);
      }).catch((err) => {
        if (seq !== this.recommendSearchSeq) return;
        this.$message.error(err.response?.data?.message || '推荐赛事搜索失败');
      }).finally(() => {
        if (seq === this.recommendSearchSeq) this.recommendLoading = false;
      });
    },
    hydrateRecommendOptions(ids) {
      this.recommendOptions = [];
      if (!ids || !ids.length) return;
      Promise.all(ids.map(id => this.$axios.post('/find_by_id', { id }).catch(() => null)))
        .then((responses) => {
          this.recommendOptions = responses
            .map(res => _.get(res, 'data.res'))
            .filter(Boolean);
        });
    },
    validateForm() {
      if (this.form.pageType === 'standard' && !this.form.eventId) return '请选择关联赛事';
      if (!this.form.eventName) return '请填写直播展示名称';
      if (!this.form.address) return '请填写比赛地点';
      if (!this.form.timeRange || this.form.timeRange.length !== 2) return '请选择比赛开始、结束时间';
      const channels = this.form.channels;
      const enabled = Object.keys(channels).filter(key => channels[key].enabled);
      if (!enabled.length) return '请至少开启一个直播渠道';
      if (channels.douyin.enabled && !channels.douyin.account && !channels.douyin.url) return '请填写抖音账号或链接';
      if (channels.wechatChannels.enabled && !channels.wechatChannels.account && !channels.wechatChannels.url) return '请填写视频号账号名称或链接';
      if (channels.bilibili.enabled && !channels.bilibili.account && !channels.bilibili.url) return '请填写 Bilibili 账号或链接';
      if (channels.video.enabled && !channels.video.url) return '请填写视频直播链接';
      if (channels.photo.enabled) {
        if (!channels.photo.items.length) return '请至少添加一组照片直播';
        for (let index = 0; index < channels.photo.items.length; index++) {
          const item = channels.photo.items[index];
          if (!item.name) return `请填写第 ${index + 1} 组照片直播名称`;
          if (!item.url) return `请填写第 ${index + 1} 组照片直播链接`;
        }
      }
      const stickerConfig = this.form.stickerConfig;
      if (stickerConfig.enabled) {
        if (Array.from(stickerConfig.topic || '').length > 20) return '贴图话题不能超过 20 个字';
        if (!stickerConfig.recommendPath) return '请填写贴图推荐链接页面';
        if (!/^\/?pages\/[A-Za-z0-9_/-]+(?:\?.*)?$/.test(stickerConfig.recommendPath)) return '贴图推荐链接必须是小程序 pages 页面路径';
        if (!stickerConfig.recommendTitle) return '请填写贴图推荐链接标题';
      }
      if (this.form.pageType === 'featured' && !this.form.livePoster) return '重点专题必须上传专题主图';
      if (this.form.pageType === 'featured' && (!channels.video.enabled || !channels.video.url)) return '重点专题必须开启并填写官方直播链接';
      if (this.form.pageType === 'featured' && !/^#[0-9a-f]{6}$/i.test(this.form.topicConfig.themeColor || '')) return '专题主题色必须为 6 位十六进制颜色';
      const schedule = this.form.topicConfig.schedule;
      if (this.form.pageType === 'featured' && schedule.mode === 'timeline') {
        if (!schedule.nodes.length) return '请至少添加一个赛程时间节点';
        for (let index = 0; index < schedule.nodes.length; index++) {
          const node = schedule.nodes[index];
          if (!node.startTime) return `请选择第 ${index + 1} 个赛程节点的开始时间`;
          if (!node.title) return `请填写第 ${index + 1} 个赛程节点的标题`;
          if (!node.content || !node.content.trim()) return `请填写第 ${index + 1} 个赛程节点的内容`;
        }
      }
      return '';
    },
    buildPayload() {
      const payload = {
        pageType: this.form.pageType,
        eventId: this.form.eventId,
        eventName: this.form.eventName,
        address: this.form.address,
        startTime: this.form.timeRange[0],
        endTime: this.form.timeRange[1],
        brandKeyword: this.form.brandKeyword,
        scheduleImage: this.form.scheduleImage,
        livePoster: this.form.livePoster,
        channels: this.form.channels,
        stickerConfig: this.form.stickerConfig,
        topicConfig: this.form.topicConfig
      };
      if (this.isEdit) payload._id = this.form._id;
      return payload;
    },
    submitForm() {
      const error = this.validateForm();
      if (error) return this.$message.error(error);

      this.saving = true;
      const url = this.isEdit ? '/live-event/update' : '/live-event/add';
      this.$axios
        .post(url, JSON.stringify(this.buildPayload()), {
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then(() => {
          this.$message.success('保存成功');
          this.dialogVisible = false;
          this.getData();
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '保存失败');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${row.eventName}」直播配置？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$axios.post('/live-event/delete', JSON.stringify({ id: row._id }), {
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        });
      }).then(() => {
        this.$message.success('删除成功');
        this.getData();
      }).catch((err) => {
        if (err === 'cancel') return;
        this.$message.error(err.response?.data?.message || '删除失败');
      });
    }
  }
};
</script>

<style scoped>
.filter-tools {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.query-input {
  width: 360px;
}

.pager {
  margin-top: 18px;
  text-align: right;
}

.channel-tag {
  margin-right: 6px;
}

.event-select,
.time-range {
  width: 100%;
}

.event-option {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 42px;
  line-height: 1.35;
}

.event-option-name {
  color: var(--text-primary, #303133);
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-option-meta {
  display: flex;
  gap: 12px;
  color: var(--text-secondary, #8492a6);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-option-status {
  color: var(--text-secondary, #8492a6);
  font-size: 12px;
  text-align: center;
}

.field-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.theme-color-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme-color-control .el-input {
  width: 150px;
}

.upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.live-image-upload,
.official-cover-upload,
.qrcode-upload {
  display: inline-block;
}

.live-image-upload >>> .el-upload {
  width: 120px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}

.official-cover-upload >>> .el-upload {
  width: 192px;
  height: 108px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}

.qrcode-upload >>> .el-upload {
  width: 96px;
  height: 96px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}

.live-image-upload >>> .el-upload:hover,
.official-cover-upload >>> .el-upload:hover,
.qrcode-upload >>> .el-upload:hover {
  border-color: #409eff;
}

.upload-placeholder,
.qrcode-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c939d;
  background: rgba(255, 255, 255, 0.02);
}

.upload-placeholder i,
.qrcode-placeholder i {
  font-size: 24px;
  margin-bottom: 8px;
}

.upload-placeholder span,
.qrcode-placeholder span {
  font-size: 12px;
  line-height: 1;
}

.preview-img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  display: block;
}

.official-cover-preview {
  width: 192px;
  height: 108px;
  object-fit: cover;
  display: block;
}

.qrcode-preview {
  width: 96px;
  height: 96px;
  object-fit: cover;
  display: block;
}

.upload-side {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.upload-tip {
  color: #909399;
  font-size: 12px;
}

.channel-block {
  padding: 12px 0;
  border-bottom: 1px solid rgba(144, 147, 153, 0.18);
}

.channel-block:last-child {
  border-bottom: 0;
}

.photo-stream-editor {
  margin: 0 0 14px 130px;
  padding: 14px 14px 2px;
  border: 1px solid rgba(144, 147, 153, 0.24);
  border-radius: 8px;
  background: rgba(144, 147, 153, 0.05);
}

.photo-stream-editor .el-form-item {
  margin-bottom: 16px;
}

.photo-stream-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  margin-bottom: 8px;
}

.schedule-node-editor {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid rgba(144, 147, 153, 0.24);
  border-radius: 8px;
  background: rgba(144, 147, 153, 0.05);
}

.schedule-node-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  margin-bottom: 8px;
}

.schedule-node-field {
  width: 100%;
  margin-bottom: 12px;
}

.schedule-node-field-label {
  margin: 2px 0 6px;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
}

.schedule-calendar-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  color: #606266;
  font-size: 13px;
}

.guide-step-editor {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid rgba(144, 147, 153, 0.24);
  border-radius: 8px;
  background: rgba(144, 147, 153, 0.05);
}

.guide-step-editor .el-input,
.guide-step-editor .el-textarea,
.guide-step-editor .upload-row {
  margin-bottom: 10px;
}

.guide-step-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.danger-text {
  color: #f56c6c;
}

.live-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.live-actions .el-button {
  margin: 0;
}

@media (max-width: 768px) {
  .photo-stream-editor {
    margin-left: 0;
  }

  .event-option-meta {
    flex-direction: column;
    gap: 2px;
    line-height: 1.3;
    white-space: normal;
  }

  .live-actions {
    flex-direction: column;
    align-items: center;
  }

  .live-actions .el-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    min-width: 40px;
    height: 36px;
    padding: 0;
  }

  .live-actions >>> .el-button i {
    margin: 0;
  }

  .live-actions >>> .el-button > span {
    display: none !important;
  }

  .live-actions >>> .el-button [class*="el-icon-"] + span {
    margin-left: 0;
  }

  .live-action-text {
    display: none !important;
  }
}
</style>

<style>
.live-event-select-dropdown .el-select-dropdown__item {
  height: auto;
  min-height: 50px;
  padding: 7px 12px;
  line-height: 1.35;
}

.live-event-select-dropdown .el-select-dropdown__wrap {
  overflow-x: hidden;
}

.live-event-select-dropdown .el-select-dropdown__list {
  padding: 6px 0;
}

@media (max-width: 768px) {
  .live-event-select-dropdown.el-select-dropdown {
    left: 12px !important;
    right: 12px !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: calc(100vw - 24px);
  }

  .live-event-select-dropdown .el-select-dropdown__wrap {
    max-height: 52vh;
    overflow-x: hidden;
  }

  .live-event-select-dropdown .el-select-dropdown__list {
    padding: 6px;
  }

  .live-event-select-dropdown .el-select-dropdown__item {
    min-height: 58px;
    padding: 8px 10px;
    white-space: normal;
  }

  .live-event-select-dropdown .event-option-name {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    word-break: break-word;
  }

  .live-event-select-dropdown .popper__arrow {
    display: none;
  }
}
</style>
